import React, { useState, useEffect } from 'react';
import { X, BookOpen, CheckCircle, Clock, AlertCircle, ShieldCheck } from 'lucide-react';
import Button from '../../../components/ui/Button';
import * as enrollmentService from '../../../services/enrollmentService';

const EnrollmentDetailsModal = ({ student, academicYearId, semesterId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [resolutionFor, setResolutionFor] = useState(null);
  const [resolutionType, setResolutionType] = useState('valid');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolutionError, setResolutionError] = useState('');
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    setLoading(true);
    try {
      const result = await enrollmentService?.getStudentEnrollments(
        student?.id,
        academicYearId,
        semesterId
      );

      if (result?.success) {
        setEnrollments(result?.data || []);
      }
    } catch (error) {
      console.error('Error loading enrollments:', error);
      setNotice({ type: 'error', text: `Failed to load enrollments: ${error?.message || 'Please try again.'}` });
    } finally {
      setLoading(false);
    }
  };

  const beginResolution = enrollment => {
    setNotice(null);
    setResolutionFor(enrollment.id);
    setResolutionType('valid');
    setResolutionNotes('');
    setResolutionError('');
  };

  const handleResolve = async enrollmentId => {
    if (resolutionType !== 'valid' && !resolutionNotes.trim()) {
      setResolutionError('Add a short reason so the decision is auditable.');
      return;
    }
    setLoading(true);
    try {
      const result = await enrollmentService?.resolveStudentUnitRegistration(
        enrollmentId,
        resolutionType,
        resolutionNotes
      );

      if (result?.success) {
        await loadEnrollments();
        setNotice({ type: 'success', text: 'Unit registration resolution saved.' });
        setResolutionFor(null);
        setResolutionNotes('');
        setResolutionError('');
      } else {
        setResolutionError(result?.error || 'Could not save the resolution.');
      }
    } catch (error) {
      console.error('Error resolving unit registration:', error);
      setResolutionError(error?.message || 'Could not save the resolution.');
    } finally {
      setLoading(false);
    }
  };

  const resolutionLabels = {
    valid: 'Confirmed valid registration',
    wrong_unit: 'Wrong unit — withdrawn',
    already_completed: 'Already completed',
    credit_transfer: 'Credit transfer recorded',
    elective_not_selected: 'Elective not selected — withdrawn'
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'enrolled':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'waiting':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case 'withdrawn':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <BookOpen className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'enrolled':
        return 'bg-green-50 text-green-800 border-green-200';
      case 'waiting':
        return 'bg-yellow-50 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'withdrawn':
        return 'bg-red-50 text-red-800 border-red-200';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  const isResolvedRegistration = enrollment => {
    const status = String(enrollment?.enrollment_status || '').toLowerCase();
    const review = String(enrollment?.review_status || '').toLowerCase();
    const resolution = String(enrollment?.resolution_type || '').toLowerCase();
    return ['withdrawn', 'removed', 'rejected', 'cancelled', 'deleted'].includes(status)
      || (review && review !== 'pending')
      || Boolean(resolution);
  };
  const activeEnrollments = (enrollments || []).filter(enrollment => !isResolvedRegistration(enrollment));
  const resolvedEnrollments = (enrollments || []).filter(isResolvedRegistration);
  const totalCredits = activeEnrollments
    .filter(e => e?.enrollment_status === 'enrolled' || e?.enrollment_status === 'completed')
    .reduce((sum, e) => sum + Number(e?.unit?.credits || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Enrollment Details</h2>
            <p className="text-green-100 text-sm mt-1">
              {student?.full_name} ({student?.student_id})
            </p>
            <p className="text-green-100/80 text-xs mt-1">HOD review: resolve wrong units, prior completion, credit transfer and elective choices.</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {notice && (
          <div
            role="status"
            className={`mx-6 mt-4 rounded-lg border px-3 py-2 text-sm ${notice.type === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}
          >
            {notice.text}
          </div>
        )}

        {/* Summary */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 px-6 py-4 border-b">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Units</p>
              <p className="text-2xl font-bold text-gray-900">{activeEnrollments.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Enrolled</p>
              <p className="text-2xl font-bold text-green-600">
                {activeEnrollments.filter(e => e?.enrollment_status === 'enrolled').length}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Waiting</p>
              <p className="text-2xl font-bold text-yellow-600">
                {activeEnrollments.filter(e => e?.enrollment_status === 'waiting').length}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Credits</p>
              <p className="text-2xl font-bold text-blue-600">{totalCredits}</p>
            </div>
          </div>
        </div>

        {/* Enrollments List */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              <span className="ml-4 text-gray-600">Loading enrollments...</span>
            </div>
          ) : activeEnrollments.length === 0 && resolvedEnrollments.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No enrollments found for this period</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeEnrollments.map(enrollment => (
                <div
                  key={enrollment?.id}
                  className={`border rounded-lg p-4 ${getStatusColor(enrollment?.enrollment_status)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getStatusIcon(enrollment?.enrollment_status)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">
                            {enrollment?.unit?.unit_code} - {enrollment?.unit?.unit_name}
                          </h3>
                          <span className="text-xs px-2 py-1 bg-white rounded-full border">
                            {enrollment?.unit?.credits} Credits
                          </span>
                          {enrollment?.review_status && enrollment.review_status !== 'pending' && (
                            <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full">
                              {resolutionLabels[enrollment.resolution_type] || enrollment.review_status}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Status:</span>
                            <span className="ml-2 font-medium capitalize">
                              {enrollment?.enrollment_status}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Enrolled:</span>
                            <span className="ml-2 font-medium">
                              {enrollment?.enrolled_at
                                ? new Date(enrollment.enrolled_at)?.toLocaleDateString()
                                : '-'}
                            </span>
                          </div>
                          {enrollment?.enrollment_status === 'waiting' && enrollment?.waiting_list_position && (
                            <div>
                              <span className="text-gray-600">Waiting Position:</span>
                              <span className="ml-2 font-medium">
                                #{enrollment?.waiting_list_position}
                              </span>
                            </div>
                          )}
                          {enrollment?.prerequisite_override && (
                            <div className="col-span-2">
                              <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                                Prerequisites Overridden
                              </span>
                              {enrollment?.override_reason && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Reason: {enrollment?.override_reason}
                                </p>
                              )}
                            </div>
                          )}
                          {enrollment?.resolution_notes && (
                            <div className="col-span-2 text-xs text-indigo-800">
                              <span className="font-semibold">HOD note:</span> {enrollment.resolution_notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {['enrolled', 'waiting'].includes(enrollment?.enrollment_status) && (
                      <Button
                        onClick={() => beginResolution(enrollment)}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                        title="Resolve exceptional unit registration"
                      >
                        <ShieldCheck className="w-4 h-4" /> Resolve
                      </Button>
                    )}
                  </div>
                  {resolutionFor === enrollment?.id && (
                    <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-950"><ShieldCheck className="h-4 w-4" />Resolve this registration</div>
                      <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
                        <label className="text-xs font-semibold text-slate-700">Decision
                          <select value={resolutionType} onChange={event => { setResolutionType(event.target.value); setResolutionError(''); }} disabled={loading} className="mt-1 w-full rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-normal">
                            <option value="valid">Confirm valid unit</option>
                            <option value="wrong_unit">Wrong unit / not for this student</option>
                            <option value="already_completed">Student already completed it</option>
                            <option value="credit_transfer">Credit transfer / prior learning</option>
                            <option value="elective_not_selected">Elective not selected</option>
                          </select>
                        </label>
                        <label className="text-xs font-semibold text-slate-700">Reason / supporting note{resolutionType !== 'valid' ? ' (required)' : ' (optional)'}
                          <textarea value={resolutionNotes} onChange={event => { setResolutionNotes(event.target.value); setResolutionError(''); }} disabled={loading} rows={2} placeholder="Explain the HOD decision for the audit trail" className="mt-1 w-full resize-none rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-normal" />
                        </label>
                      </div>
                      {resolutionError && <p className="mt-2 text-xs font-medium text-red-700">{resolutionError}</p>}
                      <div className="mt-3 flex justify-end gap-2">
                        <Button onClick={() => { setResolutionFor(null); setResolutionError(''); }} disabled={loading} className="border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700">Cancel</Button>
                        <Button onClick={() => handleResolve(enrollment.id)} disabled={loading} className="bg-indigo-700 px-3 py-2 text-xs text-white hover:bg-indigo-800">{loading ? 'Saving…' : 'Save resolution'}</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {resolvedEnrollments.length > 0 && (
                <section className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700">Resolved registrations</h3>
                      <p className="mt-0.5 text-xs text-slate-500">Kept for audit, but excluded from active units and results.</p>
                    </div>
                    <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-500">{resolvedEnrollments.length}</span>
                  </div>
                  <div className="space-y-2">
                    {resolvedEnrollments.map(enrollment => (
                      <div key={enrollment?.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
                        <div>
                          <p className="font-medium text-slate-800">{enrollment?.unit?.unit_code} - {enrollment?.unit?.unit_name}</p>
                          {enrollment?.resolution_notes && <p className="mt-0.5 text-xs text-slate-500">HOD note: {enrollment.resolution_notes}</p>}
                        </div>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold capitalize text-slate-500">{enrollment?.resolution_type || enrollment?.review_status || enrollment?.enrollment_status}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex justify-end">
          <Button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentDetailsModal;
